import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TestControl } from '../../models/test-control.interface';

/**
 * Component for rendering individual test controls
 */
@Component({
  selector: 'app-test-control',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatFormFieldModule
  ],
  template: `
    <div class="test-control" [class.disabled]="test.disabled" [class.executing]="isExecuting">
      <!-- Button Control -->
      <button 
        *ngIf="test.type === 'button'"
        mat-raised-button
        color="primary"
        [disabled]="test.disabled || isExecuting"
        (click)="executeTest()"
        [matTooltip]="test.description || ''"
        class="test-button">
        <mat-icon *ngIf="test.icon && !isExecuting">{{test.icon}}</mat-icon>
        <mat-spinner *ngIf="isExecuting" diameter="20"></mat-spinner>
        <span class="button-text">{{test.title}}</span>
      </button>

      <!-- Toggle Control -->
      <div *ngIf="test.type === 'toggle'" class="toggle-control">
        <mat-slide-toggle 
          [disabled]="test.disabled || isExecuting"
          (change)="executeTest()"
          [matTooltip]="test.description || ''">
          <mat-icon *ngIf="test.icon">{{test.icon}}</mat-icon>
          {{test.title}}
        </mat-slide-toggle>
      </div>

      <!-- Input Control -->
      <div *ngIf="test.type === 'input'" class="input-control">
        <mat-form-field appearance="outline">
          <mat-label>{{test.title}}</mat-label>
          <input 
            matInput 
            [disabled]="test.disabled || isExecuting"
            (keyup.enter)="executeTest()"
            [matTooltip]="test.description || ''"
            #inputElement>
          <mat-icon matSuffix *ngIf="test.icon">{{test.icon}}</mat-icon>
        </mat-form-field>
        <button 
          mat-icon-button 
          [disabled]="test.disabled || isExecuting"
          (click)="executeTest(inputElement.value)"
          matTooltip="Execute test">
          <mat-icon>play_arrow</mat-icon>
        </button>
      </div>

      <!-- Select Control -->
      <div *ngIf="test.type === 'select'" class="select-control">
        <mat-form-field appearance="outline">
          <mat-label>{{test.title}}</mat-label>
          <mat-select 
            [disabled]="test.disabled || isExecuting"
            (selectionChange)="executeTest($event.value)"
            [matTooltip]="test.description || ''"
            #selectElement>
            <mat-option *ngFor="let option of test.config?.['options']" [value]="option.value">
              {{option.label}}
            </mat-option>
          </mat-select>
          <mat-icon matSuffix *ngIf="test.icon">{{test.icon}}</mat-icon>
        </mat-form-field>
      </div>

      <!-- Execution Status -->
      <div *ngIf="lastExecutionResult" class="execution-result" [class]="lastExecutionResult.type">
        <mat-icon>{{getResultIcon(lastExecutionResult.type)}}</mat-icon>
        <span>{{lastExecutionResult.message}}</span>
      </div>
    </div>
  `,
  styles: [`
    .test-control {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 8px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      background: white;
      transition: all 0.2s ease;
    }

    .test-control:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    .test-control.disabled {
      opacity: 0.6;
      background: #f5f5f5;
    }

    .test-control.executing {
      background: #f0f8ff;
      border-color: #2196f3;
    }

    .test-button {
      width: 100%;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .button-text {
      font-size: 12px;
      font-weight: 500;
    }

    .toggle-control {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .input-control {
      display: flex;
      align-items: flex-end;
      gap: 8px;
    }

    .input-control mat-form-field {
      flex: 1;
    }

    .select-control {
      width: 100%;
    }

    .execution-result {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 4px;
      margin-top: 4px;
    }

    .execution-result.success {
      background: #e8f5e8;
      color: #2e7d32;
    }

    .execution-result.error {
      background: #ffebee;
      color: #c62828;
    }

    .execution-result.info {
      background: #e3f2fd;
      color: #1565c0;
    }

    mat-spinner {
      width: 20px !important;
      height: 20px !important;
    }

    mat-form-field {
      font-size: 12px;
    }
  `]
})
export class TestControlComponent {
  @Input() test!: TestControl;

  isExecuting = false;
  lastExecutionResult: { type: 'success' | 'error' | 'info', message: string } | null = null;

  /**
   * Execute the test with optional parameter
   */
  async executeTest(parameter?: any): Promise<void> {
    if (this.test.disabled || this.isExecuting) {
      console.warn(`Test '${this.test.title}' is disabled or already executing`);
      return;
    }

    this.isExecuting = true;
    this.lastExecutionResult = null;

    try {
      console.log(`🧪 Executing test: ${this.test.title}`, parameter ? { parameter } : '');

      const startTime = performance.now();

      // Execute the test with proper error boundary
      const result = await this.test.execute();

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      this.lastExecutionResult = {
        type: 'success',
        message: `✅ Completed in ${duration}ms`
      };

      console.log(`✅ Test '${this.test.title}' completed successfully in ${duration}ms`);

      // Log result if test returned something
      if (result !== undefined) {
        console.log(`📊 Test result:`, result);
      }

    } catch (error) {
      const errorMessage = this.getErrorMessage(error);

      this.lastExecutionResult = {
        type: 'error',
        message: `❌ ${errorMessage}`
      };

      console.error(`❌ Test '${this.test.title}' failed:`, error);

      // Log additional error context if available
      if (error instanceof Error && error.stack) {
        console.error('Error stack:', error.stack);
      }

    } finally {
      this.isExecuting = false;

      // Clear result after 5 seconds
      setTimeout(() => {
        this.lastExecutionResult = null;
      }, 5000);
    }
  }

  /**
   * Extract a meaningful error message from various error types
   */
  private getErrorMessage(error: any): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    return 'Unknown error occurred';
  }

  /**
   * Get the appropriate icon for execution result
   */
  getResultIcon(type: 'success' | 'error' | 'info'): string {
    switch (type) {
      case 'success': return 'check_circle';
      case 'error': return 'error';
      case 'info': return 'info';
      default: return 'help';
    }
  }
}